import React, { useState } from 'react';
import {
  Box, Typography, Button, TextField, Tabs, Tab, CircularProgress, useTheme
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PixIcon from '@mui/icons-material/Pix';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const cardGradientLight = 'linear-gradient(135deg, #ff9966, #ff5e62)';
const cardGradientDark = 'linear-gradient(135deg, #232526, #414345)';

export default function PagamentoPro() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [tab, setTab] = useState(0); // 0 = cartão, 1 = pix
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgTipo, setMsgTipo] = useState('error');

  // Cartão
  const [num, setNum] = useState('');
  const [name, setName] = useState('');
  const [val, setVal] = useState('');
  const [cvv, setCvv] = useState('');
  const [flipped, setFlipped] = useState(false);

  // PIX
  const [pixQrCode, setPixQrCode] = useState(null); // { encodedImage, payload }

  const token = localStorage.getItem('token');
  const clinicaId = user?.clinicaId || user?.clinica_id || user?.clinica?.id;
  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/$/, '');

  const getMonth = v => v.split('/')[0] || '';
  const getYear = v => { const y = v.split('/')[1] || ''; return y.length === 2 ? '20' + y : y; };
  const formatNum = v => v.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').slice(0, 19);
  const formatVal = v => {
    v = v.replace(/\D/g, '');
    if (v.length >= 3) v = v.substring(0, 2) + '/' + v.substring(2, 4);
    return v.slice(0, 5);
  };

  const authHeaders = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };

  const atualizarUserContexto = () => {
    const userAtualizado = { ...user };
    if (userAtualizado.clinica) userAtualizado.clinica.plano = 'PRO';
    else userAtualizado.plano = 'PRO';
    setUser(userAtualizado);
    localStorage.setItem('user', JSON.stringify(userAtualizado));
  };

  const handleCartao = async e => {
    e.preventDefault();
    setMsg('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/asaas/criar-assinatura`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          clinicaId,
          billingType: 'CREDIT_CARD',
          creditCard: {
            holderName: name,
            number: num.replace(/\s/g, ''),
            expiryMonth: getMonth(val),
            expiryYear: getYear(val),
            ccv: cvv
          },
          remoteIp: '127.0.0.1'
        })
      });
      const data = await res.json();
      if (res.ok && data.subscription?.id) {
        atualizarUserContexto();
        setMsgTipo('success');
        setMsg('Assinatura ativada com sucesso! Redirecionando...');
        setTimeout(() => navigate('/usuario/assinatura'), 1800);
      } else {
        setMsgTipo('error');
        setMsg(data.error?.description || data.error?.message || JSON.stringify(data.error) || 'Erro ao processar pagamento.');
      }
    } catch {
      setMsgTipo('error');
      setMsg('Erro de conexão.');
    }
    setLoading(false);
  };

  const handlePix = async () => {
    setMsg('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/asaas/criar-assinatura`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ clinicaId, billingType: 'PIX' })
      });
      const data = await res.json();
      if (res.ok && data.pixQrCode) {
        atualizarUserContexto();
        setPixQrCode(data.pixQrCode);
      } else if (res.ok && data.subscription?.id) {
        // Assinatura criada mas sem QR code ainda (será gerado pelo Asaas)
        atualizarUserContexto();
        setMsgTipo('success');
        setMsg('Assinatura PIX criada! O QR Code será enviado por e-mail. Redirecionando...');
        setTimeout(() => navigate('/usuario/assinatura'), 2500);
      } else {
        setMsgTipo('error');
        setMsg(data.error?.description || data.error?.message || JSON.stringify(data.error) || 'Erro ao gerar PIX.');
      }
    } catch {
      setMsgTipo('error');
      setMsg('Erro de conexão.');
    }
    setLoading(false);
  };

  const cardGradient = isDark ? cardGradientDark : cardGradientLight;
  const cardShadow = isDark ? '0 15px 35px rgba(30,30,30,0.4)' : '0 15px 35px rgba(255,106,0,0.2)';
  const chipColor = isDark ? '#bfa14a' : '#f39c12';
  const cardText = 'white';
  const cvvBg = isDark ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.9)';
  const cvvText = isDark ? '#fff' : '#333';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Box sx={{ display: 'flex', gap: { xs: 2, md: 7 }, alignItems: 'flex-start', maxWidth: 950, width: '90%', p: { xs: 2, md: 5 }, bgcolor: 'background.paper', borderRadius: 5, boxShadow: '0 10px 50px rgba(0,0,0,0.06)', flexDirection: { xs: 'column', md: 'row' } }}>

        {/* Preview */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {tab === 0 ? (
            <>
              <Box sx={{ perspective: 1000, width: 340, height: 200, mb: 4 }}>
                <Box sx={{ width: '100%', height: '100%', position: 'relative', transition: 'transform 0.8s cubic-bezier(0.4,0,0.2,1)', transformStyle: 'preserve-3d', transform: flipped ? 'rotateY(180deg)' : 'none' }}>
                  <Box sx={{ position: 'absolute', width: '100%', height: '100%', backfaceVisibility: 'hidden', borderRadius: 3, background: cardGradient, color: cardText, p: 3, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', boxShadow: cardShadow }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ width: 45, height: 35, bgcolor: chipColor, borderRadius: 1 }} />
                      <Typography fontWeight={700} fontSize={13}>Gerencie Odonto</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 20, letterSpacing: 2 }}>{num || '•••• •••• •••• ••••'}</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, textTransform: 'uppercase' }}>
                      <Box><Typography sx={{ opacity: 0.6, fontSize: 11 }}>Titular</Typography><Typography>{name ? name.toUpperCase() : 'NOME COMPLETO'}</Typography></Box>
                      <Box><Typography sx={{ opacity: 0.6, fontSize: 11 }}>Validade</Typography><Typography>{val || 'MM/AA'}</Typography></Box>
                    </Box>
                  </Box>
                  <Box sx={{ position: 'absolute', width: '100%', height: '100%', backfaceVisibility: 'hidden', borderRadius: 3, background: cardGradient, color: cardText, transform: 'rotateY(180deg)', display: 'flex', flexDirection: 'column', pt: 2.5, boxShadow: cardShadow }}>
                    <Box sx={{ width: '100%', height: 45, bgcolor: isDark ? '#111' : '#222' }} />
                    <Box sx={{ width: '80%', height: 35, bgcolor: cvvBg, m: '15px auto', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 2, color: cvvText }}>
                      {cvv || '•••'}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>Total a pagar</Typography>
                <Typography sx={{ fontSize: 38, fontWeight: 800 }}>R$ 129,90</Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>por mês • renovação automática</Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', pt: 2 }}>
              <Box sx={{ width: 80, height: 80, bgcolor: '#00bfa5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                <PixIcon sx={{ fontSize: 44, color: 'white' }} />
              </Box>
              <Typography variant="h6" fontWeight={700} mb={1}>PIX Recorrente</Typography>
              <Typography color="text.secondary" fontSize={14} mb={2}>Um QR Code é gerado todo mês.<br />Pague quando quiser, sem cadastro de cartão.</Typography>
              <Typography sx={{ fontSize: 38, fontWeight: 800 }}>R$ 129,90</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>por mês</Typography>
            </Box>
          )}
        </Box>

        {/* Formulário */}
        <Box sx={{ flex: 1.2 }}>
          <Typography variant="h5" fontWeight={700} mb={2}>Pagamento Seguro</Typography>

          <Tabs value={tab} onChange={(_, v) => { setTab(v); setMsg(''); setPixQrCode(null); }} sx={{ mb: 3 }}>
            <Tab icon={<CreditCardIcon />} iconPosition="start" label="Cartão de Crédito" />
            <Tab icon={<PixIcon />} iconPosition="start" label="PIX" />
          </Tabs>

          {tab === 0 && (
            <form autoComplete="off" onSubmit={handleCartao}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" mb={0.5}>Número do Cartão</Typography>
                <TextField fullWidth placeholder="0000 0000 0000 0000" value={num} onChange={e => setNum(formatNum(e.target.value))} inputProps={{ maxLength: 19 }} onFocus={() => setFlipped(false)} />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" mb={0.5}>Nome no Cartão</Typography>
                <TextField fullWidth placeholder="Ex: JOÃO A SILVA" value={name} onChange={e => setName(e.target.value)} onFocus={() => setFlipped(false)} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" mb={0.5}>Vencimento</Typography>
                  <TextField fullWidth placeholder="MM/AA" value={val} onChange={e => setVal(formatVal(e.target.value))} inputProps={{ maxLength: 5 }} onFocus={() => setFlipped(false)} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" mb={0.5}>CVV</Typography>
                  <TextField fullWidth placeholder="123" value={cvv} onChange={e => setCvv(e.target.value.replace(/\D/g, ''))} inputProps={{ maxLength: 4 }} onFocus={() => setFlipped(true)} onBlur={() => setFlipped(false)} />
                </Box>
              </Box>
              <Button variant="contained" type="submit" disabled={loading} fullWidth sx={{ bgcolor: '#ff6a00', color: 'white', borderRadius: 2, fontWeight: 700, fontSize: 16, py: 1.8, mt: 1, '&:hover': { bgcolor: '#e65c00' } }}>
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Assinar com Cartão'}
              </Button>
            </form>
          )}

          {tab === 1 && !pixQrCode && (
            <Box>
              <Typography color="text.secondary" fontSize={14} mb={3}>
                Ao clicar em "Gerar QR Code PIX", criamos sua assinatura e geramos o primeiro QR Code.
                Nos meses seguintes, um novo QR Code é enviado automaticamente por e-mail.
              </Typography>
              <Button variant="contained" disabled={loading} fullWidth onClick={handlePix} sx={{ bgcolor: '#00bfa5', color: 'white', borderRadius: 2, fontWeight: 700, fontSize: 16, py: 1.8, '&:hover': { bgcolor: '#009e8c' } }}>
                {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Gerar QR Code PIX'}
              </Button>
            </Box>
          )}

          {tab === 1 && pixQrCode && (
            <Box sx={{ textAlign: 'center' }}>
              <Typography fontWeight={700} mb={1} color="success.main">QR Code gerado!</Typography>
              <Typography fontSize={13} color="text.secondary" mb={2}>Escaneie com seu banco. O plano é ativado automaticamente após confirmação.</Typography>
              {pixQrCode.encodedImage && (
                <Box component="img" src={`data:image/png;base64,${pixQrCode.encodedImage}`} alt="QR Code PIX" sx={{ width: 200, height: 200, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }} />
              )}
              {pixQrCode.payload && (
                <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5, mb: 2 }}>
                  <Typography fontSize={11} sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>{pixQrCode.payload}</Typography>
                </Box>
              )}
              <Button variant="outlined" color="success" fullWidth onClick={() => { navigator.clipboard.writeText(pixQrCode.payload); }}>
                Copiar código PIX
              </Button>
              <Button variant="text" sx={{ mt: 1, width: '100%' }} onClick={() => navigate('/usuario/assinatura')}>
                Já paguei, voltar ao início
              </Button>
            </Box>
          )}

          {msg && (
            <Typography sx={{ mt: 2, color: msgTipo === 'success' ? 'success.main' : 'error.main', textAlign: 'center', fontSize: 14 }}>
              {msg}
            </Typography>
          )}

          {!pixQrCode && (
            <Button variant="text" sx={{ mt: 2, width: '100%' }} onClick={() => navigate(-1)}>
              Voltar
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
}
